/**
 * Frontend file-validation rules for the matter-context modal. These
 * MUST mirror the backend allowlist + size cap in
 * routers/matter_context_router.py (ALLOWED_EXTENSIONS, MAX_FILE_SIZE).
 *
 * If the backend tightens or relaxes either rule, update this module
 * too — there is no runtime-fetched constraints endpoint, so the two
 * lists are kept in sync by hand. See
 * docs_for_claude/matter_context/matter-context-frontend-design.md.
 */

import { i18n, intlTag } from '../i18n';

export const MATTER_CONTEXT_ALLOWED_EXTENSIONS = [
    '.txt',
    '.md',
    '.pdf',
    '.docx',
    '.xlsx',
    '.csv',
] as const;

export const MATTER_CONTEXT_MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024;
export const MATTER_CONTEXT_MAX_FILE_SIZE_LABEL = '50 MB';

export type MatterContextFileInvalidReason = 'unsupported_type' | 'too_large' | 'empty';

export interface MatterContextFileValidation {
    valid: boolean;
    reason?: MatterContextFileInvalidReason;
}

export function validateMatterContextFile(file: File): MatterContextFileValidation {
    const ext = extractExtension(file.name);
    if (!ext || !(MATTER_CONTEXT_ALLOWED_EXTENSIONS as readonly string[]).includes(ext)) {
        return { valid: false, reason: 'unsupported_type' };
    }
    if (file.size === 0) {
        return { valid: false, reason: 'empty' };
    }
    if (file.size > MATTER_CONTEXT_MAX_FILE_SIZE_BYTES) {
        return { valid: false, reason: 'too_large' };
    }
    return { valid: true };
}

export function describeMatterContextFileError(reason: MatterContextFileInvalidReason): string {
    switch (reason) {
        case 'unsupported_type':
            return i18n.global.t('matterContext.fileError.unsupportedType');
        case 'too_large':
            return i18n.global.t('matterContext.fileError.tooLarge', {
                limit: MATTER_CONTEXT_MAX_FILE_SIZE_LABEL,
            });
        case 'empty':
            return i18n.global.t('matterContext.fileError.empty');
    }
}

/**
 * Returns the lowercased extension including the leading dot, matching
 * the backend's `Path(filename).suffix.lower()` semantics. Returns the
 * empty string for files with no extension.
 */
function extractExtension(filename: string): string {
    if (!filename) return '';
    const lastDot = filename.lastIndexOf('.');
    if (lastDot <= 0 || lastDot === filename.length - 1) return '';
    return filename.slice(lastDot).toLowerCase();
}

/**
 * SHA-256 hex digest of the file's bytes, matching the backend's
 * `hashlib.sha256(content).hexdigest()`. Used to pre-flag a staged file
 * as a duplicate of one already processed for the matter before upload.
 * The backend re-checks authoritatively, so a hashing failure here is
 * non-fatal — the caller should treat it as "unknown, not a duplicate".
 */
export async function hashFileSha256(file: File): Promise<string> {
    const buffer = await file.arrayBuffer();
    const digest = await crypto.subtle.digest('SHA-256', buffer);
    return Array.from(new Uint8Array(digest))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
}

/** Comma-separated form for an `<input type="file" accept="…">` attribute. */
export const MATTER_CONTEXT_ACCEPT_ATTR = MATTER_CONTEXT_ALLOWED_EXTENSIONS.join(',');

/**
 * Human-readable list, e.g. ".txt, .md, .pdf, .docx, .xlsx, and .csv".
 * Uses Intl.ListFormat so the conjunction follows the active locale rather
 * than a hardcoded English "and".
 *
 * Intl.ListFormat is widely available at runtime (all modern browsers, Node
 * 12+) but isn't in the project's TS lib typings, so we declare the slice we
 * use locally rather than widening the global `lib` config.
 */
interface ListFormatCtor {
    new (
        locales?: string,
        options?: { style?: 'long' | 'short' | 'narrow'; type?: 'conjunction' | 'disjunction' | 'unit' },
    ): { format(items: Iterable<string>): string };
}

export function formatAllowedExtensionsLabel(): string {
    const exts = [...MATTER_CONTEXT_ALLOWED_EXTENSIONS];
    const ListFormat = (Intl as unknown as { ListFormat: ListFormatCtor }).ListFormat;
    return new ListFormat(intlTag(), { style: 'long', type: 'conjunction' }).format(exts);
}
